/**
 * GLSL sources.
 *
 * The terrain shader unpacks the 20-byte vertex format written by the mesher,
 * combines the baked per-vertex colour (biome tint * ambient occlusion * face
 * shading) with the dynamic skylight/blocklight levels, and applies distance
 * fog. Blocklight is given a warm tint so a torch-lit cave reads differently
 * from a moonlit field at the same brightness.
 */

export const TERRAIN_VS = `#version 300 es
precision highp float;
precision highp int;

layout(location = 0) in uvec4 aPosLight;  // x, y, z (1/32 block), sky|block<<4
layout(location = 1) in uvec4 aUvTex;     // u, v (1/64 tile), texLayer, normal
layout(location = 2) in vec4 aColor;      // baked tint * AO * face shade

uniform mat4 uViewProj;
uniform vec3 uChunkOrigin;
uniform float uSkyBrightness;
uniform float uAmbient;
uniform vec3 uFogColor;
uniform float uFogStart;
uniform float uFogEnd;
uniform vec3 uCameraPos;

out vec3 vColor;
out vec2 vUv;
flat out uint vTexLayer;
out float vFog;

void main() {
  vec3 local = vec3(aPosLight.xyz) / 32.0 - vec3(1.0, 0.0, 1.0);
  vec3 world = uChunkOrigin + local;

  uint packed = aPosLight.w;
  float sky = float(packed & 15u) / 15.0;
  float block = float((packed >> 4u) & 15u) / 15.0;

  // Vanilla's lightmap is strongly non-linear: the last few levels carry most
  // of the visible difference, and there is a small ambient floor so caves are
  // navigable rather than pitch black.
  float skyTerm = sky * uSkyBrightness;
  float level = max(skyTerm, block);
  // Vanilla's lightmap is steeply non-linear; this is a slightly gentler
  // version of it. The ambient floor is per-dimension: the Nether and the End
  // have no sun, so without one they would be unreadably dark.
  float curve = uAmbient + (1.0 - uAmbient) * pow(level, 1.35);

  vec3 warm = mix(vec3(1.0), vec3(1.35, 1.08, 0.78), clamp(block - skyTerm * 0.8, 0.0, 1.0));
  vColor = aColor.rgb * curve * warm;

  vUv = vec2(aUvTex.xy) / 64.0;
  vTexLayer = aUvTex.z;

  float dist = distance(world, uCameraPos);
  vFog = clamp((dist - uFogStart) / max(1.0, uFogEnd - uFogStart), 0.0, 1.0);

  gl_Position = uViewProj * vec4(world, 1.0);
}
`;

export const TERRAIN_FS = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2DArray;

in vec3 vColor;
in vec2 vUv;
flat in uint vTexLayer;
in float vFog;

uniform sampler2DArray uAtlas;
uniform vec3 uFogColor;
uniform float uAlphaCutoff;

out vec4 fragColor;

void main() {
  vec4 tex = texture(uAtlas, vec3(vUv, float(vTexLayer)));
  if (tex.a < uAlphaCutoff) discard;
  vec3 color = tex.rgb * vColor;
  color = mix(color, uFogColor, vFog);
  fragColor = vec4(color, tex.a);
}
`;

/** Sky dome: a vertical gradient plus a sun and moon billboard. */
export const SKY_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vNdc;
void main() {
  vNdc = aPos;
  gl_Position = vec4(aPos, 1.0, 1.0);
}
`;

export const SKY_FS = `#version 300 es
precision highp float;
in vec2 vNdc;
uniform mat4 uInvViewProj;
uniform vec3 uCameraPos;
uniform vec3 uSkyTop;
uniform vec3 uSkyHorizon;
uniform vec3 uSunDir;
uniform float uSunIntensity;
uniform float uStarAmount;
out vec4 fragColor;

// Cheap hash-based starfield.
float hash(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}

void main() {
  vec4 far = uInvViewProj * vec4(vNdc, 1.0, 1.0);
  vec3 dir = normalize(far.xyz / far.w - uCameraPos);

  float h = clamp(dir.y * 1.4 + 0.15, 0.0, 1.0);
  vec3 color = mix(uSkyHorizon, uSkyTop, pow(h, 0.65));

  // Stars fade in as the sun sets.
  if (uStarAmount > 0.01 && dir.y > -0.1) {
    vec3 cell = floor(dir * 180.0);
    float s = hash(cell);
    if (s > 0.9975) {
      float twinkle = 0.6 + 0.4 * hash(cell + 3.7);
      color += vec3(twinkle) * uStarAmount * smoothstep(-0.1, 0.25, dir.y);
    }
  }

  // Sun and moon discs.
  float sunDot = dot(dir, uSunDir);
  color += vec3(1.0, 0.95, 0.8) * smoothstep(0.9985, 0.9995, sunDot) * 1.6 * max(uSunIntensity, 0.15);
  color += vec3(1.0, 0.9, 0.7) * pow(max(sunDot, 0.0), 64.0) * 0.25 * uSunIntensity;
  float moonDot = dot(dir, -uSunDir);
  color += vec3(0.85, 0.88, 1.0) * smoothstep(0.9990, 0.9997, moonDot) * 1.1;

  fragColor = vec4(color, 1.0);
}
`;

/** Solid-colour lines, used for the block selection box and debug shapes. */
export const LINE_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec3 aPos;
uniform mat4 uViewProj;
void main() {
  gl_Position = uViewProj * vec4(aPos, 1.0);
}
`;

export const LINE_FS = `#version 300 es
precision highp float;
uniform vec4 uColor;
out vec4 fragColor;
void main() {
  fragColor = uColor;
}
`;

/** Entities: simple lit boxes with a per-instance colour and texture layer. */
export const ENTITY_VS = `#version 300 es
precision highp float;
precision highp int;
layout(location = 0) in vec3 aPos;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec2 aUv;

uniform mat4 uViewProj;
uniform mat4 uModel;
uniform float uLight;
uniform vec3 uTint;
uniform vec3 uFogColor;
uniform float uFogStart;
uniform float uFogEnd;
uniform vec3 uCameraPos;

out vec2 vUv;
out vec3 vShade;
out float vFog;

void main() {
  vec4 world = uModel * vec4(aPos, 1.0);
  vec3 n = normalize(mat3(uModel) * aNormal);
  float shade = 0.55 + 0.45 * max(dot(n, normalize(vec3(0.4, 1.0, 0.25))), 0.0);
  vShade = uTint * shade * uLight;
  vUv = aUv;
  vFog = clamp((distance(world.xyz, uCameraPos) - uFogStart) / max(1.0, uFogEnd - uFogStart), 0.0, 1.0);
  gl_Position = uViewProj * world;
}
`;

export const ENTITY_FS = `#version 300 es
precision highp float;
precision highp sampler2DArray;
in vec2 vUv;
in vec3 vShade;
in float vFog;
uniform sampler2DArray uAtlas;
uniform float uTexLayer;
uniform float uUseTexture;
uniform vec3 uFogColor;
out vec4 fragColor;
void main() {
  vec4 tex = uUseTexture > 0.5 ? texture(uAtlas, vec3(vUv, uTexLayer)) : vec4(1.0);
  if (tex.a < 0.5) discard;
  vec3 color = mix(tex.rgb * vShade, uFogColor, vFog);
  fragColor = vec4(color, 1.0);
}
`;

/** Screen-space quads for the HUD (hotbar icons, crosshair, item sprites). */
export const SPRITE_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
layout(location = 1) in vec2 aUv;
uniform vec2 uScreen;
uniform vec4 uRect;   // x, y, w, h in pixels
uniform vec4 uUvRect; // u0, v0, u1, v1
out vec2 vUv;
void main() {
  vec2 px = uRect.xy + aPos * uRect.zw;
  vec2 ndc = vec2(px.x / uScreen.x * 2.0 - 1.0, 1.0 - px.y / uScreen.y * 2.0);
  vUv = mix(uUvRect.xy, uUvRect.zw, aUv);
  gl_Position = vec4(ndc, 0.0, 1.0);
}
`;

export const SPRITE_FS = `#version 300 es
precision highp float;
precision highp sampler2DArray;
in vec2 vUv;
uniform sampler2DArray uAtlas;
uniform float uLayer;
uniform vec4 uColor;
uniform float uUseTexture;
out vec4 fragColor;
void main() {
  vec4 tex = uUseTexture > 0.5 ? texture(uAtlas, vec3(vUv, uLayer)) : vec4(1.0);
  fragColor = tex * uColor;
  if (fragColor.a < 0.01) discard;
}
`;
